import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { PageHeader } from '@components/layout/Breadcrumb';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import { 
  FiSearch, 
  FiFilter, 
  FiGrid, 
  FiList,
  FiCheckCircle, 
  FiAward, 
  FiShield,
  FiPhone,
  FiMail,
  FiChevronRight,
  FiPackage
} from 'react-icons/fi';
import { getProducts, searchProducts } from '@services/productService';

/**
 * Products Page Component - Alibaba Style
 * Features: Grid/List view, Search, Filters, API integration
 */
const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [filters, setFilters] = useState({
    category: searchParams.get('category') || 'all',
    packaging: searchParams.get('packaging') || 'all',
    certification: searchParams.get('certification') || 'all',
    sortBy: searchParams.get('sort') || 'featured'
  });
  const [pagination, setPagination] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: 12,
    total: 0
  });

  const breadcrumbs = [{ label: 'Products', path: '/products' }];

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      setError(null);
      
      try {
        const params = {
          page: pagination.page,
          limit: pagination.limit,
          ...(filters.category !== 'all' && { category: filters.category }),
          ...(filters.packaging !== 'all' && { packaging: filters.packaging }),
          ...(filters.certification !== 'all' && { certification: filters.certification }),
          sort: filters.sortBy
        };

        let response;
        if (searchQuery) {
          response = await searchProducts(searchQuery, params);
        } else {
          response = await getProducts(params);
        }

        setProducts(response.data || response.products || []);
        setPagination(prev => ({
          ...prev,
          total: response.total || response.data?.length || 0
        }));
      } catch (err) {
        setError('Failed to load products. Please try again later.');
        console.error('Error loading products:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchQuery, filters, pagination.page]);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('search', searchQuery);
    if (filters.category !== 'all') params.set('category', filters.category);
    if (filters.packaging !== 'all') params.set('packaging', filters.packaging);
    if (filters.certification !== 'all') params.set('certification', filters.certification);
    if (filters.sortBy !== 'featured') params.set('sort', filters.sortBy);
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    
    setSearchParams(params);
  }, [searchQuery, filters, pagination.page, setSearchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({ ...prev, [filterType]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const totalPages = Math.ceil(pagination.total / pagination.limit);

  return (
    <div className="products-page">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&family=Crimson+Pro:wght@600;700&display=swap');

        .products-page {
          font-family: 'DM Sans', sans-serif;
          background: linear-gradient(135deg, #fdfbf7 0%, #fff5e6 100%);
          min-height: 100vh;
        }

        /* Hero Section */
        .products-hero {
          background: linear-gradient(135deg, #2d5016 0%, #4a7c2c 100%);
          color: white;
          padding: 3rem 0 2rem;
          position: relative;
          overflow: hidden;
        }

        .products-hero::before {
          content: '';
          position: absolute;
          top: 0;
          right: 0;
          width: 50%;
          height: 100%;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
          opacity: 0.5;
        }

        .products-hero .container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
          position: relative;
          z-index: 1;
        }

        .products-hero h1 {
          font-family: 'Crimson Pro', serif;
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 0.5rem;
          animation: fadeInUp 0.6s ease-out;
        }

        .products-hero p {
          font-size: 1.1rem;
          opacity: 0.95;
          max-width: 600px;
          animation: fadeInUp 0.6s ease-out 0.1s backwards;
        }

        /* Search Bar */
        .search-section {
          background: white;
          padding: 1.5rem 0;
          border-bottom: 1px solid #e8e8e8;
          position: sticky;
          top: 0;
          z-index: 100;
          box-shadow: 0 2px 8px rgba(0,0,0,0.05);
        }

        .search-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .search-bar {
          display: flex;
          gap: 1rem;
          align-items: center;
          margin-bottom: 1rem;
        }

        .search-input-wrapper {
          flex: 1;
          position: relative;
          max-width: 600px;
        }

        .search-input-wrapper input {
          width: 100%;
          padding: 0.875rem 3rem 0.875rem 1.25rem;
          border: 2px solid #e0e0e0;
          border-radius: 8px;
          font-size: 1rem;
          transition: all 0.3s ease;
          font-family: 'DM Sans', sans-serif;
        }

        .search-input-wrapper input:focus {
          outline: none;
          border-color: #4a7c2c;
          box-shadow: 0 0 0 3px rgba(74, 124, 44, 0.1);
        }

        .search-icon {
          position: absolute;
          right: 1rem;
          top: 50%;
          transform: translateY(-50%);
          color: #666;
        }

        .view-toggle {
          display: flex;
          gap: 0.5rem;
          background: #f5f5f5;
          padding: 0.25rem;
          border-radius: 8px;
        }

        .view-toggle button {
          padding: 0.5rem 0.75rem;
          border: none;
          background: transparent;
          border-radius: 6px;
          cursor: pointer;
          color: #666;
          transition: all 0.2s;
        }

        .view-toggle button.active {
          background: white;
          color: #4a7c2c;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        /* Filters */
        .filters-row {
          display: flex;
          gap: 1rem;
          align-items: center;
          flex-wrap: wrap;
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .filter-group label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #666;
        }

        .filter-group select {
          padding: 0.5rem 2rem 0.5rem 0.75rem;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          background: white;
          font-size: 0.875rem;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }

        .filter-group select:focus {
          outline: none;
          border-color: #4a7c2c;
        }

        .results-count {
          margin-left: auto;
          font-size: 0.875rem;
          color: #666;
        }

        /* Main Content */
        .products-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 2rem 1rem;
        }

        /* Product Grid */
        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 1.5rem;
          margin-bottom: 3rem;
        }

        .products-grid.list-view {
          grid-template-columns: 1fr;
        }

        /* Product Card - Grid View */
        .product-card {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid #f0f0f0;
          display: flex;
          flex-direction: column;
          animation: fadeInUp 0.5s ease-out backwards;
        }

        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.1);
          border-color: #4a7c2c;
        }

        .product-image-wrapper {
          position: relative;
          padding-top: 100%;
          background: linear-gradient(135deg, #f8f8f8 0%, #e8e8e8 100%);
          overflow: hidden;
        }

        .product-image-wrapper img {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.5s ease;
        }

        .product-card:hover .product-image-wrapper img {
          transform: scale(1.05);
        }

        .product-badges {
          position: absolute;
          top: 0.75rem;
          left: 0.75rem;
          display: flex;
          flex-direction: column;
          gap: 0.5rem;
          z-index: 2;
        }

        .badge {
          padding: 0.35rem 0.75rem;
          border-radius: 6px;
          font-size: 0.75rem;
          font-weight: 600;
          background: white;
          color: #4a7c2c;
          box-shadow: 0 2px 8px rgba(0,0,0,0.15);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .product-info {
          padding: 1.25rem;
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .product-name {
          font-family: 'Crimson Pro', serif;
          font-size: 1.25rem;
          font-weight: 700;
          color: #2d5016;
          margin-bottom: 0.5rem;
          line-height: 1.3;
        }

        .product-description {
          color: #666;
          font-size: 0.9rem;
          line-height: 1.5;
          margin-bottom: 1rem;
          flex: 1;
        }

        .product-features {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .feature-tag {
          padding: 0.35rem 0.75rem;
          background: #f0f7eb;
          color: #2d5016;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 500;
        }

        .product-packaging {
          font-size: 0.85rem;
          color: #666;
          margin-bottom: 1rem;
          padding-top: 1rem;
          border-top: 1px solid #f0f0f0;
        }

        .product-packaging strong {
          color: #2d5016;
          display: block;
          margin-bottom: 0.35rem;
        }

        .packaging-options {
          display: flex;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .packaging-option {
          background: #fff;
          border: 1px solid #e0e0e0;
          padding: 0.25rem 0.6rem;
          border-radius: 6px;
          font-size: 0.8rem;
        }

        .product-actions {
          display: flex;
          gap: 0.75rem;
          margin-top: auto;
        }

        .btn {
          flex: 1;
          padding: 0.75rem 1rem;
          border-radius: 8px;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          border: none;
          font-family: 'DM Sans', sans-serif;
        }

        .btn-primary {
          background: linear-gradient(135deg, #4a7c2c 0%, #2d5016 100%);
          color: white;
        }

        .btn-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 6px 16px rgba(74, 124, 44, 0.3);
        }

        .btn-outline {
          background: white;
          color: #4a7c2c;
          border: 2px solid #4a7c2c;
        }

        .btn-outline:hover {
          background: #4a7c2c;
          color: white;
        }

        /* List View Specific Styles */
        .products-grid.list-view .product-card {
          flex-direction: row;
        }

        .products-grid.list-view .product-image-wrapper {
          width: 250px;
          padding-top: 0;
          min-height: 250px;
        }

        .products-grid.list-view .product-image-wrapper img {
          position: static;
        }

        .products-grid.list-view .product-info {
          flex: 1;
          padding: 1.5rem;
        }

        /* Loading State */
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          min-height: 400px;
        }

        .spinner {
          width: 50px;
          height: 50px;
          border: 4px solid #f0f0f0;
          border-top-color: #4a7c2c;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        /* Error State */
        .error-container {
          text-align: center;
          padding: 3rem 1rem;
          color: #d32f2f;
        }

        /* Empty State */
        .empty-state {
          text-align: center;
          padding: 4rem 1rem;
          color: #666;
        }

        .empty-state-icon {
          font-size: 4rem;
          color: #ddd;
          margin-bottom: 1rem;
        }

        /* Pagination */
        .pagination {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 0.5rem;
          margin: 3rem 0;
        }

        .pagination button {
          padding: 0.5rem 1rem;
          border: 1px solid #e0e0e0;
          background: white;
          border-radius: 6px;
          cursor: pointer;
          transition: all 0.2s;
          font-family: 'DM Sans', sans-serif;
        }

        .pagination button:hover:not(:disabled) {
          background: #4a7c2c;
          color: white;
          border-color: #4a7c2c;
        }

        .pagination button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .pagination button.active {
          background: #4a7c2c;
          color: white;
          border-color: #4a7c2c;
        }

        /* Features Section */
        .features-section {
          background: white;
          padding: 4rem 0;
          margin-top: 4rem;
          border-top: 1px solid #e8e8e8;
        }

        .features-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 2rem;
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
        }

        .feature-card {
          text-align: center;
          padding: 2rem;
          animation: fadeInUp 0.6s ease-out backwards;
        }

        .feature-icon {
          width: 60px;
          height: 60px;
          margin: 0 auto 1.5rem;
          background: linear-gradient(135deg, #4a7c2c 0%, #2d5016 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-size: 1.5rem;
        }

        .feature-card h3 {
          font-family: 'Crimson Pro', serif;
          font-size: 1.3rem;
          color: #2d5016;
          margin-bottom: 0.75rem;
        }

        .feature-card p {
          color: #666;
          line-height: 1.6;
        }

        /* Bulk CTA Section */
        .bulk-cta {
          background: linear-gradient(135deg, #2d5016 0%, #4a7c2c 100%);
          color: white;
          padding: 3rem 0;
          margin-top: 4rem;
        }

        .bulk-cta-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 1rem;
          display: grid;
          grid-template-columns: 2fr 1fr;
          gap: 3rem;
          align-items: center;
        }

        .bulk-info h2 {
          font-family: 'Crimson Pro', serif;
          font-size: 2rem;
          margin-bottom: 1rem;
        }

        .bulk-info ul {
          list-style: none;
          padding: 0;
          margin: 1.5rem 0;
        }

        .bulk-info li {
          padding: 0.5rem 0;
          padding-left: 1.5rem;
          position: relative;
        }

        .bulk-info li::before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #90EE90;
          font-weight: bold;
        }

        .bulk-actions {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .bulk-actions h3 {
          font-family: 'Crimson Pro', serif;
          font-size: 1.5rem;
          margin-bottom: 0.5rem;
        }

        .bulk-actions p {
          opacity: 0.9;
          margin-bottom: 1rem;
        }

        .contact-buttons {
          display: flex;
          flex-direction: column;
          gap: 1rem;
        }

        .contact-btn {
          padding: 1rem 1.5rem;
          background: white;
          color: #4a7c2c;
          border-radius: 8px;
          text-decoration: none;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .contact-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(0,0,0,0.2);
        }

        /* Animations */
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        /* Responsive */
        @media (max-width: 768px) {
          .products-hero h1 {
            font-size: 2rem;
          }

          .search-bar {
            flex-direction: column;
          }

          .search-input-wrapper {
            max-width: 100%;
          }

          .view-toggle {
            align-self: flex-end;
          }

          .filters-row {
            flex-direction: column;
            align-items: stretch;
          }

          .results-count {
            margin-left: 0;
          }

          .products-grid.list-view .product-card {
            flex-direction: column;
          }

          .products-grid.list-view .product-image-wrapper {
            width: 100%;
          }

          .bulk-cta-content {
            grid-template-columns: 1fr;
          }

          .feature-card:nth-child(n + 2) {
            animation-delay: 0.1s;
          }
          .feature-card:nth-child(n + 3) {
            animation-delay: 0.2s;
          }
        }

        /* Stagger animation for product cards */
        .product-card:nth-child(1) { animation-delay: 0s; }
        .product-card:nth-child(2) { animation-delay: 0.05s; }
        .product-card:nth-child(3) { animation-delay: 0.1s; }
        .product-card:nth-child(4) { animation-delay: 0.15s; }
        .product-card:nth-child(5) { animation-delay: 0.2s; }
        .product-card:nth-child(6) { animation-delay: 0.25s; }
        .product-card:nth-child(7) { animation-delay: 0.3s; }
        .product-card:nth-child(8) { animation-delay: 0.35s; }
      `}</style>

      {/* Hero Section */}
      <div className="products-hero">
        <div className="container">
          <PageHeader title="Our Products" breadcrumbs={breadcrumbs} />
          <h1>Premium White Maize Flour</h1>
          <p>
            Discover our range of high-quality, UNBS-certified maize flour products. 
            From households to institutions, we provide excellence in every package.
          </p>
        </div>
      </div>

      {/* Search & Filters Section */}
      <div className="search-section">
        <div className="search-container">
          <div className="search-bar">
            <form onSubmit={handleSearch} className="search-input-wrapper">
              <input
                type="text"
                placeholder="Search products..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <FiSearch className="search-icon" size={20} />
            </form>

            <div className="view-toggle">
              <button
                className={viewMode === 'grid' ? 'active' : ''}
                onClick={() => setViewMode('grid')}
                type="button"
              >
                <FiGrid size={20} />
              </button>
              <button
                className={viewMode === 'list' ? 'active' : ''}
                onClick={() => setViewMode('list')}
                type="button"
              >
                <FiList size={20} />
              </button>
            </div>
          </div>

          <div className="filters-row">
            <div className="filter-group">
              <FiFilter size={18} />
              <label>Category:</label>
              <select 
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
              >
                <option value="all">All Categories</option>
                <option value="retail">Retail</option>
                <option value="wholesale">Wholesale</option>
                <option value="industrial">Industrial</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Packaging:</label>
              <select
                value={filters.packaging}
                onChange={(e) => handleFilterChange('packaging', e.target.value)}
              >
                <option value="all">All Sizes</option>
                <option value="1kg">1kg</option>
                <option value="2kg">2kg</option>
                <option value="5kg">5kg</option>
                <option value="10kg">10kg</option>
                <option value="25kg">25kg</option>
                <option value="50kg">50kg</option>
              </select>
            </div>

            <div className="filter-group">
              <label>Sort by:</label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              >
                <option value="featured">Featured</option>
                <option value="name_asc">Name (A-Z)</option>
                <option value="name_desc">Name (Z-A)</option>
                <option value="newest">Newest</option>
              </select>
            </div>

            {!loading && (
              <div className="results-count">
                {pagination.total} {pagination.total === 1 ? 'product' : 'products'} found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Products Content */}
      <div className="products-content">
        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="spinner"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <h2>{error}</h2>
            <button 
              className="btn btn-primary"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && products.length === 0 && (
          <div className="empty-state">
            <div className="empty-state-icon">
              <FiPackage />
            </div>
            <h2>No products found</h2>
            <p>Try adjusting your search or filters</p>
            <button 
              className="btn btn-primary"
              onClick={() => {
                setSearchQuery('');
                setFilters({
                  category: 'all',
                  packaging: 'all',
                  certification: 'all',
                  sortBy: 'featured'
                });
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Products Grid */}
        {!loading && !error && products.length > 0 && (
          <>
            <div className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
              {products.map((product) => (
                <ProductCard key={product.id} product={product} viewMode={viewMode} />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={pagination.page === 1}
                >
                  Previous
                </button>
                
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  // Show first page, last page, current page, and pages around current
                  if (
                    pageNum === 1 ||
                    pageNum === totalPages ||
                    (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))}
                        className={pagination.page === pageNum ? 'active' : ''}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                    return <span key={pageNum}>...</span>;
                  }
                  return null;
                })}
                
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page === totalPages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Features Section */}
      <div className="features-section">
        <div className="features-grid">
          {[
            {
              icon: FiCheckCircle,
              title: 'Consistent Quality',
              description: 'Every batch is tested and meets strict quality standards for uniform excellence.',
            },
            {
              icon: FiShield,
              title: 'Full Traceability',
              description: 'From farm to package, we track every step ensuring complete transparency.',
            },
            {
              icon: FiAward,
              title: 'Ethical Sourcing',
              description: 'Supporting over 1,000 local farmers with fair prices and sustainable practices.',
            },
          ].map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div key={index} className="feature-card">
                <div className="feature-icon">
                  <Icon />
                </div>
                <h3>{feature.title}</h3>
                <p>{feature.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bulk Orders CTA */}
      <div className="bulk-cta">
        <div className="bulk-cta-content">
          <div className="bulk-info">
            <h2>Bulk Orders Available</h2>
            <p>
              We supply premium white maize flour in bulk quantities to wholesalers, 
              retailers, and institutions. Contact us for competitive pricing and 
              delivery arrangements.
            </p>
            <ul>
              <li>Competitive wholesale prices</li>
              <li>Reliable delivery schedules</li>
              <li>Quality guaranteed</li>
            </ul>
          </div>

          <div className="bulk-actions">
            <h3>Interested in Bulk Orders?</h3>
            <p>Get in touch with our sales team for pricing and availability</p>
            <div className="contact-buttons">
              <a href="tel:+256786142168" className="contact-btn">
                <FiPhone /> Call: +256 786 142168
              </a>
              <Link to="/contact" className="contact-btn">
                <FiMail /> Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * ProductCard Component
 */
const ProductCard = ({ product, viewMode }) => {
  return (
    <div className="product-card">
      <div className="product-image-wrapper">
        <img
          src={product.image || product.imageUrl || '/placeholder-product.jpg'}
          alt={product.name}
          onError={(e) => {
            e.target.src = '/placeholder-product.jpg';
          }}
        />
        <div className="product-badges">
          {product.certifications?.map((cert, index) => (
            <span key={index} className="badge">
              {cert}
            </span>
          )) || <span className="badge">UNBS Certified</span>}
        </div>
      </div>

      <div className="product-info">
        <h3 className="product-name">{product.name}</h3>
        <p className="product-description">
          {product.description || product.shortDescription}
        </p>

        {product.features && product.features.length > 0 && (
          <div className="product-features">
            {product.features.slice(0, 3).map((feature, index) => (
              <span key={index} className="feature-tag">
                {feature}
              </span>
            ))}
          </div>
        )}

        {product.packaging && product.packaging.length > 0 && (
          <div className="product-packaging">
            <strong>Available in:</strong>
            <div className="packaging-options">
              {product.packaging.map((pack, index) => (
                <span key={index} className="packaging-option">
                  {pack}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="product-actions">
          <Link
            to={`/products/${product.id || product.slug}`}
            className="btn btn-primary"
          >
            View Details <FiChevronRight />
          </Link>
          <Link
            to={`/contact?product=${product.id || product.slug}`}
            className="btn btn-outline"
          >
            Get Quote
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Products;